import { mande, type MandeError } from 'mande'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useErrorHandler } from '@/composables/useErrorHandler'

interface DictionaryItem {
  id?: string
  title: string
  definition: string
  content: string
  slug: string
}

interface DictionaryGroup {
  letter: string
  items: DictionaryItem[]
}

export function useDictionaryGroups() {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/dictionary`)
  const language = useI18n().locale.value.split('-')[0]
  const groups = ref<DictionaryGroup[] | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)

  const fetchDictionaryGroups = async (letter: string = '') => {
    try {
      loading.value = true
      const response = await api.get<{ payload: DictionaryGroup[] }>('', {
        headers: {
          'Accept-Language': language,
        },
        query: letter ? { letter } : {},
      })
      groups.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError
    } finally {
      loading.value = false
    }
  }

  // Do not auto-fetch; let callers decide when to fetch to avoid duplicate requests

  return {
    groups,
    fetchDictionaryGroups,
    loading,
    error,
  }
}

export function useDictionaryBySlug(slug: string) {
  const config = useRuntimeConfig()
  const api = mande(`${config.public.apiBaseUrl}/dictionary`)
  const language = useI18n().locale.value.split('-')[0]
  const dictionary = ref<DictionaryItem | null>(null)
  const loading = ref<boolean>(true)
  const error = ref<MandeError | null>(null)
  const { handleHttpError } = useErrorHandler()

  const fetchDictionaryBySlug = async () => {
    try {
      const response = await api.get<{ payload: DictionaryItem }>(slug, {
        headers: {
          'Accept-Language': language,
        },
      })
      dictionary.value = response.payload
    } catch (apiError) {
      error.value = apiError as MandeError

      // Error handler ile hatayı işle
      handleHttpError(apiError)
    } finally {
      loading.value = false
    }
  }

  fetchDictionaryBySlug().then(() => {})

  return {
    dictionary,
    loading,
    error,
  }
}
